import React, { useCallback, useEffect, useRef, useState } from "react";
import { Popover } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const HEX6 = /^#?[0-9A-Fa-f]{6}$/;

export const parseHex = (value) => {
  const raw = String(value || "").trim();
  if (!HEX6.test(raw)) return null;
  const hex = raw.startsWith("#") ? raw : `#${raw}`;
  return hex.toUpperCase();
};

const hexToRgb = (hex) => {
  const h = hex.slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

const rgbToHex = (r, g, b) =>
  `#${[r, g, b]
    .map((n) =>
      Math.round(Math.max(0, Math.min(255, n)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("")
    .toUpperCase()}`;

const rgbToHsv = (r, g, b) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;
  if (d !== 0) {
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
        break;
      case gn:
        h = ((bn - rn) / d + 2) / 6;
        break;
      default:
        h = ((rn - gn) / d + 4) / 6;
        break;
    }
  }
  return { h: h * 360, s, v };
};

const hsvToRgb = (h, s, v) => {
  const hh = (((h % 360) + 360) % 360) / 60;
  const i = Math.floor(hh);
  const f = hh - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  const map = [
    [v, t, p],
    [q, v, p],
    [p, v, t],
    [p, q, v],
    [t, p, v],
    [v, p, q],
  ][i % 6];
  return { r: map[0] * 255, g: map[1] * 255, b: map[2] * 255 };
};

const hsvFromHex = (hex) => {
  const parsed = parseHex(hex) || "#000000";
  const { r, g, b } = hexToRgb(parsed);
  return rgbToHsv(r, g, b);
};

const hexFromHsv = ({ h, s, v }) => {
  const { r, g, b } = hsvToRgb(h, s, v);
  return rgbToHex(r, g, b);
};

const usePointerDrag = (onMove) => {
  const dragging = useRef(false);
  const moveRef = useRef(onMove);
  moveRef.current = onMove;

  const onPointerMove = useCallback((event) => {
    if (!dragging.current) return;
    moveRef.current(event);
  }, []);

  const stop = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", stop);
  }, [onPointerMove]);

  const start = useCallback(
    (event, element) => {
      event.preventDefault();
      dragging.current = true;
      moveRef.current(event, element);
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", stop);
    },
    [onPointerMove, stop]
  );

  useEffect(() => () => stop(), [stop]);

  return start;
};

/**
 * Promotions-only color field: visual HS picker + Hex input (no RGB/HSL formats).
 */
export const HexColorPicker = ({ label, value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const safeValue = parseHex(value) || "#000000";
  const [hsv, setHsv] = useState(() => hsvFromHex(safeValue));
  const [hexDraft, setHexDraft] = useState(safeValue);
  const hsvRef = useRef(hsv);
  const wasOpenRef = useRef(false);
  const svRef = useRef(null);
  const hueRef = useRef(null);

  useEffect(() => {
    hsvRef.current = hsv;
  }, [hsv]);

  // Sync HSV from props when closed or on open. While open, keep local hsv as
  // source of truth so grey/white (S≈0) does not wipe hue via hex round-trip.
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (open && !justOpened && hexFromHsv(hsvRef.current) === safeValue) {
      return;
    }

    const next = hsvFromHex(safeValue);
    setHsv(next);
    setHexDraft(safeValue);
  }, [open, safeValue]);

  const commitHsv = useCallback(
    (next) => {
      hsvRef.current = next;
      setHsv(next);
      const hex = hexFromHsv(next);
      setHexDraft(hex);
      onChange(hex);
    },
    [onChange]
  );

  const updateSvFromEvent = useCallback(
    (event) => {
      const el = svRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
      const y = Math.min(Math.max(event.clientY - rect.top, 0), rect.height);
      commitHsv({
        h: hsvRef.current.h,
        s: rect.width ? x / rect.width : 0,
        v: rect.height ? 1 - y / rect.height : 0,
      });
    },
    [commitHsv]
  );

  const updateHueFromEvent = useCallback(
    (event) => {
      const el = hueRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
      const h = rect.width ? (x / rect.width) * 360 : 0;
      commitHsv({
        h,
        s: hsvRef.current.s,
        v: hsvRef.current.v,
      });
    },
    [commitHsv]
  );

  const startSvDrag = usePointerDrag(updateSvFromEvent);
  const startHueDrag = usePointerDrag(updateHueFromEvent);

  const handleHexChange = (event) => {
    let next = event.target.value.toUpperCase().replace(/[^#0-9A-F]/g, "");
    if (next && !next.startsWith("#")) next = `#${next}`;
    if (next.length > 7) next = next.slice(0, 7);
    setHexDraft(next);
    const parsed = parseHex(next);
    if (parsed) {
      setHsv(hsvFromHex(parsed));
      onChange(parsed);
    }
  };

  const handleHexBlur = () => {
    const parsed = parseHex(hexDraft);
    if (parsed) {
      setHexDraft(parsed);
      return;
    }
    setHexDraft(safeValue);
  };

  const hueColor = hexFromHsv({ h: hsv.h, s: 1, v: 1 });
  const currentColor = hexFromHsv(hsv);

  return (
    <>
      <button
        type="button"
        className="email-design-color-row email-design-color-row--button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span>
          {label} <em>*</em>
        </span>
        <span className="email-design-color-row__value">
          <i style={{ background: safeValue }} />
          {safeValue}
          <ChevronRightIcon fontSize="small" />
        </span>
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "hex-color-picker" }}
      >
        <div
          ref={svRef}
          className="hex-color-picker__sv"
          style={{ backgroundColor: hueColor }}
          onPointerDown={(e) => startSvDrag(e)}
          role="presentation"
        >
          <div className="hex-color-picker__sv-white" />
          <div className="hex-color-picker__sv-black" />
          <span
            className="hex-color-picker__sv-thumb"
            style={{
              left: `${hsv.s * 100}%`,
              top: `${(1 - hsv.v) * 100}%`,
              background: currentColor,
            }}
          />
        </div>

        <div
          ref={hueRef}
          className="hex-color-picker__hue"
          onPointerDown={(e) => startHueDrag(e)}
          role="presentation"
        >
          <span
            className="hex-color-picker__hue-thumb"
            style={{ left: `${(hsv.h / 360) * 100}%` }}
          />
        </div>

        <label className="hex-color-picker__hex">
          <span>Hex</span>
          <input
            type="text"
            spellCheck={false}
            autoComplete="off"
            value={hexDraft}
            onChange={handleHexChange}
            onBlur={handleHexBlur}
            maxLength={7}
            aria-label={`${label} hex color`}
          />
          <i style={{ background: currentColor }} aria-hidden />
        </label>
      </Popover>
    </>
  );
};
